import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Country, State } from 'country-state-city';
import { stripePromise } from '../../utils/stripe';
import { useNetwork } from '../../utils/network';
import { useUser } from '../../contexts/UserContext';
import {
  PT,
  dialogPaperSx,
  fieldSx,
  ghostBtnSx,
  primaryBtnSx
} from '../../components/portalUi';
import { selectMenuProps, selectSx } from '../../components/portalSelect';
import {
  aud,
  bidderLabel,
  cannotBidWords,
  dollarsToCents,
  quickBids,
  type AuctionView
} from './auction';
import AuctionCountdown from './AuctionCountdown';

/* The auction on the member's Physical Cards page: the lot sheet — the same catalogue
 * page the public pages show — with what only a member gets: the top three, their own
 * standing and deposit, a bid, and for the winner the payment.
 *
 * `PaymentForm` is handed in by the page (PhysicalCards exports it): importing it here
 * would make the two files import each other. The shipping address is collected here
 * in a compact form of the page's own fields; the server reads the same keys. */
const GOLD = '#e8c877';
const GOLD_SOFT = 'rgba(232,200,119,0.14)';
const GOLD_BORDER = 'rgba(232,200,119,0.5)';
const RED = '#ff8a8a';
const INK = '#eef6fb';
const MUTED = '#8ea3b3';
const mono = '800 10px ui-monospace, Menlo, Consolas, monospace';
const stencil = {
  font: mono,
  letterSpacing: '0.22em',
  textTransform: 'uppercase' as const,
  color: MUTED
};
const goldBtn = {
  ...primaryBtnSx,
  borderRadius: '4px',
  background: GOLD,
  color: '#0b0f14',
  boxShadow: `0 10px 30px -12px ${GOLD}`,
  '&:hover': { background: '#f0d58c' }
};

type Shipping = {
  name: string;
  email: string;
  phone: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
};
type PaymentFormProps = {
  amount: number;
  billing: {
    name: string;
    email: string;
    phone?: string;
    address: {
      line1: string;
      line2?: string;
      city: string;
      state?: string;
      postal_code: string;
      country: string;
    };
  };
  onDone: () => void;
  onBack: () => void;
};

const words = (e: unknown, fallback: string): string => {
  const err = e as { data?: { message?: string }; message?: string } | null;
  return err?.data?.message || err?.message || fallback;
};

export const AuctionPanel: React.FC<{
  PaymentForm: React.FC<PaymentFormProps>;
}> = ({ PaymentForm }) => {
  const { get, post } = useNetwork();
  const { user, setUser } = useUser();
  const [view, setView] = useState<AuctionView | null>(null);
  const [failed, setFailed] = useState(false);

  // useNetwork() hands out a new `get` on every render; an effect keyed on it
  // re-ran on every render and each load re-rendered — a request storm. Pin it.
  const getRef = useRef(get);
  getRef.current = get;
  const load = useCallback(async () => {
    try {
      const v = (await getRef.current(
        '/auctions/current/me',
        true
      )) as AuctionView;
      setView(v);
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    load();
    const t = window.setInterval(load, 20000);
    return () => window.clearInterval(t);
  }, [load]);

  // ── bidding ────────────────────────────────────────────────────────────────
  const [amount, setAmount] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [bidError, setBidError] = useState<string | null>(null);
  const [bidNote, setBidNote] = useState<string | null>(null);
  // while the member is typing, the 20-second refresh must not touch the field:
  // a half-typed "203" is below the minimum and was being snapped back to it
  const editing = useRef(false);

  const lot = view?.auction || null;
  const you = view?.you || null;
  const nextMin = view?.nextMinCents ?? lot?.openingCents ?? 0;
  const cents = dollarsToCents(amount);
  const lotId = lot?.id ?? null;

  useEffect(() => {
    // offer the next acceptable bid once the lot is known, and lift the field to it
    // when bidding moves above what is typed — never while the field has focus
    if (lotId == null || editing.current) return;
    setAmount((cur) => {
      const c = dollarsToCents(cur);
      return c == null || c < nextMin ? String(nextMin / 100) : cur;
    });
  }, [lotId, nextMin]);

  const placeBid = async () => {
    if (!lot || cents == null) return;
    setPlacing(true);
    setBidError(null);
    try {
      const v = (await post(
        `/auctions/${lot.id}/bids`,
        { amountCents: cents },
        true
      )) as AuctionView & { extended?: boolean };
      setView(v);
      setConfirming(false);
      setBidNote(
        v.extended
          ? 'Bid placed — and because it landed in the final minutes, the auction has been extended.'
          : 'Bid placed. We will email you if someone outbids you.'
      );
      if (user) setUser({ ...user });
    } catch (e) {
      const err = e as {
        data?: { code?: string; minCents?: number; message?: string };
      };
      setConfirming(false);
      if (err?.data?.code === 'TOO_LOW' && err.data.minCents) {
        setAmount(String(err.data.minCents / 100));
        setBidError(
          `Someone has bid since. The bid must now be at least ${aud(err.data.minCents)}.`
        );
      } else {
        setBidError(words(e, 'Could not place the bid.'));
      }
      load();
    } finally {
      setPlacing(false);
    }
  };

  // ── the winner's payment ──────────────────────────────────────────────────
  const [payStep, setPayStep] = useState<'idle' | 'shipping' | 'pay' | 'done'>(
    'idle'
  );
  const [shipping, setShipping] = useState<Shipping>({
    name: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '',
    email: user?.email || '',
    phone: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    postcode: '',
    country: 'AU'
  });
  const [payError, setPayError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [payment, setPayment] = useState<{
    clientSecret: string;
    amount: number;
    publishableKey?: string;
  } | null>(null);
  const countries = useMemo(() => Country.getAllCountries(), []);
  const states = useMemo(
    () => State.getStatesOfCountry(shipping.country) || [],
    [shipping.country]
  );
  const activeStripe = useMemo(
    () =>
      payment?.publishableKey
        ? loadStripe(payment.publishableKey)
        : stripePromise,
    [payment?.publishableKey]
  );
  const set =
    (k: keyof Shipping) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setShipping((s) => ({ ...s, [k]: e.target.value }));

  const startPayment = async () => {
    const offer = you?.offer;
    if (!offer) return;
    const missing = (
      ['name', 'email', 'line1', 'city', 'postcode', 'country'] as const
    ).filter((k) => !shipping[k].trim());
    if (missing.length) {
      setPayError(
        'Please fill in your name, email, address line 1, city, postcode and country.'
      );
      return;
    }
    setStarting(true);
    setPayError(null);
    try {
      const res = (await post(
        `/auctions/offers/${offer.id}/payment-intent`,
        { shipping, attemptId: `${offer.id}-${Date.now()}` },
        true
      )) as { clientSecret: string; amount: number; publishableKey?: string };
      setPayment(res);
      setPayStep('pay');
    } catch (e) {
      setPayError(words(e, 'Could not start the payment.'));
    } finally {
      setStarting(false);
    }
  };

  if (failed && !view) return null;
  if (!lot || lot.status === 'draft' || lot.status === 'cancelled') return null;

  const top = view?.top || [];
  const open = lot.live;
  const offer = you?.offer || null;
  const blocked = cannotBidWords(you);
  const closes = new Date(lot.endsAt).toLocaleString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit'
  });

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: '6px',
        border: `1px solid ${GOLD}66`,
        outline: `1px solid ${GOLD}2e`,
        outlineOffset: 5,
        background: `repeating-linear-gradient(135deg, ${GOLD}08 0 2px, transparent 2px 14px), linear-gradient(180deg, #0d141d, #070b11)`,
        boxShadow: `0 40px 90px -50px ${GOLD}88, inset 0 1px 0 rgba(255,255,255,0.04)`,
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          md: lot.imageUrl ? '5fr 8fr' : '1fr'
        },
        overflow: 'hidden',
        '@keyframes incAuctionPulse': {
          '0%, 100%': { boxShadow: `0 0 0 0 ${GOLD}66` },
          '50%': { boxShadow: `0 0 0 6px ${GOLD}00` }
        }
      }}
    >
      {lot.imageUrl && (
        <Box sx={{ position: 'relative', minHeight: { xs: 240, md: 420 } }}>
          <Box
            component="img"
            src={lot.imageUrl}
            alt=""
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: {
                xs: 'linear-gradient(180deg, transparent 55%, #070b11 100%)',
                md: 'linear-gradient(90deg, transparent 70%, #070b11 100%)'
              }
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 26,
              left: -46,
              transform: 'rotate(-35deg)',
              width: 220,
              py: 0.6,
              textAlign: 'center',
              background: open ? GOLD : '#2a3340',
              color: open ? '#0b0f14' : '#cfd9e2',
              font: mono,
              letterSpacing: '0.3em',
              textTransform: 'uppercase',
              boxShadow: '0 6px 18px rgba(0,0,0,0.5)'
            }}
          >
            {open
              ? 'Live auction'
              : lot.status === 'settled'
                ? 'Sold'
                : 'Closed'}
          </Box>
        </Box>
      )}

      <Box
        sx={{
          p: { xs: 2.5, md: 3.5 },
          pt: { xs: 1.5, md: 3.5 },
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <Typography
          sx={{
            ...stencil,
            letterSpacing: '0.28em',
            color: GOLD,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          {open && (
            <Box
              component="span"
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: GOLD,
                animation: 'incAuctionPulse 1.6s ease-out infinite'
              }}
            />
          )}
          First edition · one of one ·{' '}
          {open
            ? 'bidding open'
            : lot.status === 'settled'
              ? 'sold'
              : 'bidding closed'}
        </Typography>

        <Typography
          className="inc-orbitron"
          sx={{
            fontSize: { xs: 22, md: 26 },
            fontWeight: 800,
            color: INK,
            lineHeight: 1.12,
            textWrap: 'balance'
          }}
        >
          {lot.title}
        </Typography>
        {lot.description && (
          <Typography
            sx={{
              color: MUTED,
              fontSize: 14,
              lineHeight: 1.6,
              whiteSpace: 'pre-line'
            }}
          >
            {lot.description}
          </Typography>
        )}

        {/* the particulars, ruled */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', sm: 'auto auto 1fr' },
            gap: { xs: 2, sm: 3 },
            py: 2,
            borderTop: `1px solid ${GOLD}33`,
            borderBottom: `1px solid ${GOLD}33`
          }}
        >
          <Box>
            <Typography sx={stencil}>
              {top[0] ? 'Leading bid' : 'Opening bid'}
            </Typography>
            <Typography
              className="inc-orbitron"
              sx={{
                mt: 0.75,
                fontSize: { xs: 26, md: 30 },
                fontWeight: 800,
                color: GOLD,
                lineHeight: 1,
                fontVariantNumeric: 'tabular-nums'
              }}
            >
              {aud(top[0] ? top[0].amountCents : lot.openingCents)}
            </Typography>
            <Typography
              sx={{
                mt: 0.75,
                color: '#cfd9e2',
                fontSize: 13.5,
                fontWeight: 600,
                whiteSpace: 'nowrap'
              }}
            >
              {top[0] ? bidderLabel(top[0].bidder) : 'No bids yet'}
            </Typography>
          </Box>
          <Box
            sx={{ borderLeft: { sm: `1px solid ${GOLD}33` }, pl: { sm: 3 } }}
          >
            <Typography sx={stencil}>You</Typography>
            <Typography
              className="inc-orbitron"
              sx={{
                mt: 0.75,
                fontSize: { xs: 26, md: 30 },
                fontWeight: 800,
                color: you?.rank === 1 ? GOLD : INK,
                lineHeight: 1,
                fontVariantNumeric: 'tabular-nums'
              }}
            >
              {you?.highestBidCents != null ? aud(you.highestBidCents) : '—'}
            </Typography>
            <Typography
              sx={{
                mt: 0.75,
                color: MUTED,
                fontSize: 13.5,
                whiteSpace: 'nowrap'
              }}
            >
              {you?.rank
                ? `#${you.rank}${you.hold?.status === 'held' ? ' · deposit held' : you.hold?.status === 'released' ? ' · deposit released' : ''}`
                : 'no bid yet'}
            </Typography>
          </Box>
          <Box
            sx={{
              gridColumn: { xs: '1 / -1', sm: 'auto' },
              borderLeft: { sm: `1px solid ${GOLD}33` },
              pl: { sm: 3 }
            }}
          >
            <Typography sx={{ ...stencil, mb: 0.75 }}>
              {open ? 'Closes in' : 'Closed'}
            </Typography>
            {open ? (
              <AuctionCountdown
                endsAt={lot.endsAt}
                compact
                tone={{
                  ink: INK,
                  label: MUTED,
                  box: 'rgba(255,255,255,0.04)',
                  border: `${GOLD}33`
                }}
              />
            ) : (
              <Typography sx={{ color: '#cfd9e2', fontSize: 14 }}>
                {closes}
              </Typography>
            )}
            {open && (
              <Typography sx={{ mt: 0.75, color: MUTED, fontSize: 12.5 }}>
                {closes} · a bid in the last{' '}
                {Math.round(lot.extensionSeconds / 60)} min extends it
              </Typography>
            )}
          </Box>
        </Box>

        {/* the top three */}
        <Box>
          <Typography sx={{ ...stencil, mb: 1 }}>Top bids</Typography>
          {top.length === 0 ? (
            <Typography sx={{ color: MUTED, fontSize: 13.5 }}>
              Nobody has bid yet.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {top.map((t) => (
                <Box
                  key={t.rank}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 0.9,
                    borderBottom: `1px solid ${GOLD}22`,
                    '&:last-of-type': { borderBottom: 'none' }
                  }}
                >
                  <Typography
                    sx={{
                      ...stencil,
                      color: t.rank === 1 ? GOLD : MUTED,
                      width: 26
                    }}
                  >
                    #{t.rank}
                  </Typography>
                  <Typography
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      color: INK,
                      fontSize: 14,
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {bidderLabel(t.bidder)}
                  </Typography>
                  <Typography
                    sx={{
                      color: t.rank === 1 ? GOLD : INK,
                      fontWeight: 800,
                      fontVariantNumeric: 'tabular-nums'
                    }}
                  >
                    {aud(t.amountCents)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* the bid */}
        {open &&
          you &&
          (blocked ? (
            <Typography
              sx={{
                color: you.reason === 'LEADING' ? GOLD : MUTED,
                fontSize: 14,
                fontWeight: 600
              }}
            >
              {blocked}
            </Typography>
          ) : (
            <Box>
              <Typography sx={{ ...stencil, mb: 1 }}>
                Your bid · whole dollars · at least {aud(nextMin)}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  flexWrap: 'wrap',
                  alignItems: 'center'
                }}
              >
                <TextField
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value);
                    setBidError(null);
                    setBidNote(null);
                  }}
                  onFocus={() => {
                    editing.current = true;
                  }}
                  onBlur={() => {
                    editing.current = false;
                  }}
                  size="small"
                  sx={{ ...fieldSx, width: 180 }}
                  inputProps={{
                    inputMode: 'numeric',
                    'aria-label': 'Your bid in Australian dollars'
                  }}
                  InputProps={{
                    startAdornment: (
                      <Typography sx={{ color: MUTED, mr: 0.5 }}>A$</Typography>
                    )
                  }}
                />
                {quickBids(nextMin, lot.minRaiseCents).map((q) => (
                  <Button
                    key={q}
                    size="small"
                    onClick={() => {
                      setAmount(String(q / 100));
                      setBidError(null);
                    }}
                    sx={{ ...ghostBtnSx, px: 1.5, borderRadius: '4px' }}
                  >
                    {aud(q)}
                  </Button>
                ))}
                <Button
                  onClick={() => {
                    if (cents == null || cents < nextMin) {
                      setBidError(`The bid must be at least ${aud(nextMin)}.`);
                      return;
                    }
                    setConfirming(true);
                  }}
                  disabled={placing}
                  startIcon={<GavelIcon />}
                  sx={{ ...goldBtn, px: 2.5 }}
                >
                  Place bid
                </Button>
              </Box>
              {bidError && (
                <Typography sx={{ mt: 1, color: RED, fontSize: 13.5 }}>
                  {bidError}
                </Typography>
              )}
              {bidNote && !bidError && (
                <Typography sx={{ mt: 1, color: PT.tealText, fontSize: 13.5 }}>
                  {bidNote}
                </Typography>
              )}
              <Typography
                sx={{ mt: 1, color: MUTED, fontSize: 12.5, lineHeight: 1.5 }}
              >
                Placing a bid holds {lot.depositInc.toLocaleString('en-AU')} INC
                of your balance as a deposit for as long as you are in the top
                three. It is released if you drop out, and when the set is paid
                for. A winner who does not complete the purchase forfeits the
                deposit.
              </Typography>
            </Box>
          ))}

        {/* the winner */}
        {offer && (
          <Box
            sx={{
              mt: 0.5,
              p: 2,
              borderRadius: '4px',
              border: `1px solid ${offer.status === 'offered' ? GOLD_BORDER : `${GOLD}33`}`,
              background:
                offer.status === 'offered'
                  ? GOLD_SOFT
                  : 'rgba(255,255,255,0.02)'
            }}
          >
            <Typography
              sx={{
                ...stencil,
                color: GOLD,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <EmojiEventsIcon sx={{ fontSize: 16 }} />{' '}
              {offer.status === 'paid'
                ? 'Paid — thank you'
                : offer.status === 'backed_out'
                  ? 'Offer withdrawn'
                  : 'You won'}
            </Typography>
            {offer.status === 'offered' && payStep === 'idle' && (
              <>
                <Typography
                  sx={{ mt: 1, color: INK, fontSize: 14.5, lineHeight: 1.55 }}
                >
                  The set is yours for <strong>{aud(offer.amountCents)}</strong>
                  {offer.rank > 1
                    ? ' (the bidder ahead of you did not complete the purchase)'
                    : ''}
                  .
                  {offer.payBy
                    ? ` Please pay by ${new Date(offer.payBy).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })}.`
                    : ' We will confirm the payment deadline with you.'}{' '}
                  Your deposit is released the moment payment is in.
                </Typography>
                <Box
                  sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}
                >
                  <Button onClick={() => setPayStep('shipping')} sx={goldBtn}>
                    Pay by card
                  </Button>
                </Box>
                <Typography sx={{ mt: 1, color: MUTED, fontSize: 12.5 }}>
                  Prefer a bank transfer? Contact us and we will send the
                  details; the deposit is released once it has arrived.
                </Typography>
              </>
            )}
            {offer.status === 'offered' && payStep === 'shipping' && (
              <Box sx={{ mt: 1.5 }}>
                <Typography
                  sx={{
                    ...stencil,
                    color: GOLD,
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center'
                  }}
                >
                  <LocalShippingOutlinedIcon sx={{ fontSize: 16 }} /> Where
                  should we ship it?
                </Typography>
                <Box
                  sx={{
                    mt: 1.5,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 1.5
                  }}
                >
                  <TextField
                    label="Full name *"
                    value={shipping.name}
                    onChange={set('name')}
                    sx={{ ...fieldSx, gridColumn: { sm: '1 / -1' } }}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    label="Email *"
                    value={shipping.email}
                    onChange={set('email')}
                    sx={fieldSx}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    label="Phone"
                    value={shipping.phone}
                    onChange={set('phone')}
                    sx={fieldSx}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    label="Address line 1 *"
                    value={shipping.line1}
                    onChange={set('line1')}
                    sx={{ ...fieldSx, gridColumn: { sm: '1 / -1' } }}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    label="Address line 2"
                    value={shipping.line2}
                    onChange={set('line2')}
                    sx={{ ...fieldSx, gridColumn: { sm: '1 / -1' } }}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    label="City / suburb *"
                    value={shipping.city}
                    onChange={set('city')}
                    sx={fieldSx}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    label="Postcode *"
                    value={shipping.postcode}
                    onChange={set('postcode')}
                    sx={fieldSx}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    select
                    label="Country *"
                    value={shipping.country}
                    onChange={(e) =>
                      setShipping((s) => ({
                        ...s,
                        country: e.target.value,
                        state: ''
                      }))
                    }
                    sx={selectSx}
                    SelectProps={{ MenuProps: selectMenuProps }}
                    size="small"
                    fullWidth
                  >
                    {countries.map((c) => (
                      <MenuItem key={c.isoCode} value={c.isoCode}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  {states.length > 0 ? (
                    <TextField
                      select
                      label="State / region"
                      value={shipping.state}
                      onChange={set('state')}
                      sx={selectSx}
                      SelectProps={{ MenuProps: selectMenuProps }}
                      size="small"
                      fullWidth
                    >
                      {states.map((s) => (
                        <MenuItem key={s.isoCode} value={s.name}>
                          {s.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    <TextField
                      label="State / region"
                      value={shipping.state}
                      onChange={set('state')}
                      sx={fieldSx}
                      size="small"
                      fullWidth
                    />
                  )}
                </Box>
                {payError && (
                  <Typography sx={{ mt: 1, color: RED, fontSize: 13.5 }}>
                    {payError}
                  </Typography>
                )}
                <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                  <Button onClick={() => setPayStep('idle')} sx={ghostBtnSx}>
                    Back
                  </Button>
                  <Button
                    onClick={startPayment}
                    disabled={starting}
                    sx={goldBtn}
                  >
                    {starting
                      ? 'One moment…'
                      : `Continue to pay ${aud(offer.amountCents)}`}
                  </Button>
                </Box>
              </Box>
            )}
            {offer.status === 'offered' && payStep === 'pay' && payment && (
              <Box sx={{ mt: 1.5 }}>
                <Elements
                  stripe={activeStripe}
                  options={{
                    clientSecret: payment.clientSecret,
                    appearance: {
                      theme: 'night',
                      variables: {
                        colorPrimary: PT.teal,
                        colorBackground: PT.surface,
                        colorText: PT.text,
                        borderRadius: '10px',
                        fontFamily: 'Montserrat, sans-serif'
                      }
                    }
                  }}
                >
                  <PaymentForm
                    amount={payment.amount}
                    billing={{
                      name: shipping.name,
                      email: shipping.email,
                      phone: shipping.phone || undefined,
                      address: {
                        line1: shipping.line1,
                        line2: shipping.line2 || undefined,
                        city: shipping.city,
                        state: shipping.state || undefined,
                        postal_code: shipping.postcode,
                        country: shipping.country
                      }
                    }}
                    onDone={() => {
                      setPayStep('done');
                      setTimeout(load, 2500);
                    }}
                    onBack={() => setPayStep('shipping')}
                  />
                </Elements>
              </Box>
            )}
            {(offer.status === 'paid' || payStep === 'done') && (
              <Typography
                sx={{ mt: 1, color: INK, fontSize: 14.5, lineHeight: 1.55 }}
              >
                Your payment is in. Your deposit has been released and we will
                be in touch about delivery.
              </Typography>
            )}
            {offer.status === 'backed_out' && (
              <Typography sx={{ mt: 1, color: MUTED, fontSize: 14 }}>
                This offer was withdrawn and the set has been offered to the
                next bidder.
              </Typography>
            )}
          </Box>
        )}
      </Box>

      <Dialog
        open={confirming}
        onClose={() => !placing && setConfirming(false)}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ color: INK, fontWeight: 800 }}>
          Place this bid?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: INK, fontSize: 15 }}>
            You are bidding{' '}
            <strong style={{ color: GOLD }}>
              {cents != null ? aud(cents) : ''}
            </strong>{' '}
            on {lot.title}.
          </Typography>
          <Typography
            sx={{ mt: 1.5, color: MUTED, fontSize: 13.5, lineHeight: 1.55 }}
          >
            {you?.hold?.status === 'held'
              ? 'Your deposit is already held, so nothing more is held for this bid.'
              : `${lot.depositInc.toLocaleString('en-AU')} INC of your balance will be held as a deposit while you are in the top three. You cannot withdraw or spend it until it is released.`}{' '}
            A bid cannot be withdrawn. If you win and do not complete the
            purchase, the deposit is forfeited.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setConfirming(false)}
            disabled={placing}
            sx={ghostBtnSx}
          >
            Cancel
          </Button>
          <Button onClick={placeBid} disabled={placing} sx={goldBtn}>
            {placing ? 'Placing…' : 'Confirm bid'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuctionPanel;
